import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import type { PageDto } from '@/lib/pagination';
import { useAuth } from '@/lib/auth/AuthContext';

export interface ActionExecutionOut {
  action: string;
  completed_at: string | null;
  created_at: string;
  destination_id: string | null;
  document_id: string | null;
  encounter_id: string;
  executed_by: string | null;
  id: string;
  organization_id: string;
  result: Record<string, unknown> | null;
  started_at: string | null;
  status: string;
  template_id: string | null;
}

export interface DocumentPatientSummary {
  id: string;
  mrn: string;
  name: string | null;
}

export interface DocumentTemplateSummary {
  id: string;
  name: string;
}

export interface GeneratedDocumentOut {
  source_encounter_ids?: string[];
  amendment_in_progress?: boolean;
  signed_version_id?: string | null;
  version_number?: number | null;
  signed_at?: string | null;
  signer_name?: string | null;
  title?: string | null;
  additional_context: Record<string, unknown> | null;
  content: string;
  created_at: string;
  created_by: string | null;
  deleted_at: string | null;
  generated_at: string;
  id: string;
  organization_id: string;
  patient: DocumentPatientSummary | null;
  /** null when the document is in the Inbox (Plan 06). */
  patient_id: string | null;
  status: string;
  tags?: import('./tags-api').TagDto[];
  template: DocumentTemplateSummary | null;
  template_id: string | null;
  /** Temporal dashboard deep link for the document's latest workflow; non-null only for superusers. */
  temporal_url: string | null;
  updated_at: string;
}

export interface GeneratedDocumentSourceOut {
  document_id: string;
  encounter_id: string;
  id: string;
  linked_at: string;
}

export type PaginatedDocuments = PageDto<GeneratedDocumentOut>;

export function useActionExecutions(params?: { encounter_id?: string; status?: string }) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId,
    queryKey: ['action-executions', orgId, params],
    queryFn: async () => {
      const { data } = await clientApi.get<PageDto<ActionExecutionOut>>(`${orgUrl(orgId!)}/action-executions`, {
        params,
      });
      return data;
    },
    // Generation is asynchronous: POST /documents/generate returns 202 the
    // moment the workflow is scheduled, long before a document row exists. This
    // is the one query whose row DOES exist at that point, so it is what drives
    // progress. Stops polling as soon as nothing is in flight.
    refetchInterval: (query) => {
      const executions = query.state.data?.data;
      return executions?.some((e) => e.status === 'pending' || e.status === 'running') ? 5000 : false;
    },
  });
}

export function useAssignDocumentToPatient() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId, patient_id }: { documentId: string; patient_id: string }) => {
      const { data } = await clientApi.post<GeneratedDocumentOut>(`${orgUrl(orgId!)}/documents/${documentId}/assign`, {
        patient_id,
      });
      return data;
    },
    onSuccess: (_, variables) => {
      // Both the Inbox query and the target patient's chart query are scoped
      // under ['documents', orgId, ...] — invalidate the whole subtree so the
      // row leaves the Inbox and appears under the patient timeline.
      queryClient.invalidateQueries({ queryKey: ['documents', orgId] });
      queryClient.invalidateQueries({ queryKey: ['documents', orgId, variables.documentId] });
    },
  });
}

export function useBulkDeleteDocuments() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', orgId] });
    },
    mutationFn: async (documentIds: string[]) => {
      const results = await Promise.allSettled(
        documentIds.map((id) => clientApi.delete(`${orgUrl(orgId!)}/documents/${id}`)),
      );
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length > 0) {
        throw new Error(`Failed to delete ${failed.length} of ${documentIds.length} documents`);
      }
    },
  });
}

export function useDeleteDocument() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', orgId] });
      queryClient.invalidateQueries({ queryKey: ['encounters', orgId] });
      queryClient.invalidateQueries({ queryKey: ['signing-document', orgId] });
    },
    mutationFn: async (documentId: string) => {
      await clientApi.delete(`${orgUrl(orgId!)}/documents/${documentId}`);
    },
  });
}

export function useDocument(documentId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId && !!documentId,
    queryKey: ['documents', orgId, documentId],
    queryFn: async () => {
      const { data } = await clientApi.get<GeneratedDocumentOut>(`${orgUrl(orgId!)}/documents/${documentId}`);
      return data;
    },
  });
}

export function useDocuments(
  params?: {
    limit?: number;
    offset?: number;
    patient_id?: string;
    status?: string;
    /** When true, only return orphan (Inbox) documents — patient_id IS NULL. */
    inbox?: boolean;
  },
  enabled = true,
) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId && enabled,
    queryKey: ['documents', orgId, params],
    queryFn: async () => {
      const { data } = await clientApi.get<PaginatedDocuments>(`${orgUrl(orgId!)}/documents`, { params });
      return data;
    },
  });
}

export function useDocumentSources(documentId: string | undefined) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;

  return useQuery({
    enabled: !!orgId && !!documentId,
    queryKey: ['documents', orgId, documentId, 'sources'],
    queryFn: async () => {
      const { data } = await clientApi.get<GeneratedDocumentSourceOut[]>(
        `${orgUrl(orgId!)}/documents/${documentId}/sources`,
      );
      return data;
    },
  });
}

/** How long to follow a generation before giving up on it. */
const GENERATION_POLL_INTERVAL_MS = 4000;
const GENERATION_POLL_TIMEOUT_MS = 10 * 60 * 1000;

export function useGenerateDocument() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      template_id: string;
      patient_id: string | null;
      encounter_ids: string[];
      additional_context?: Record<string, unknown> | null;
    }) => {
      // 202 + an ActionExecution, not the document. Generation now runs the
      // full Temporal pipeline (cleanup -> generate -> tag -> proofread), so
      // the document appears once the workflow finishes rather than inline.
      const { data: result } = await clientApi.post<ActionExecutionOut>(`${orgUrl(orgId!)}/documents/generate`, data);
      return result;
    },
    onSuccess: async (execution, variables) => {
      const encounterId = variables.encounter_ids[0];

      // TanStack matches query keys by PREFIX. The encounter page reads its
      // documents under ['encounters', orgId, encounterId, 'documents'], which
      // ['documents', orgId] does NOT match — so the surface these dialogs are
      // launched from was never refetched at all.
      const refreshLists = () => {
        queryClient.invalidateQueries({ queryKey: ['documents', orgId] });
        queryClient.invalidateQueries({ queryKey: ['action-executions', orgId] });
        if (encounterId) {
          queryClient.invalidateQueries({ queryKey: ['encounters', orgId, encounterId, 'documents'] });
        }
      };

      refreshLists();

      // Generation is asynchronous now: the POST returns 202 the moment the
      // workflow is scheduled, and the document row does not exist yet. So the
      // invalidation above cannot show it — the mutation follows the execution
      // it started and refreshes again when the document actually exists.
      //
      // Owned here rather than on each page because the two dialogs are mounted
      // on different surfaces (the encounter page and the document viewer, which
      // the Inbox also opens); a per-page effect would miss one.
      await waitForExecution(orgId!, execution.id, refreshLists);
    },
  });
}

export function useUpdateDocument() {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const queryClient = useQueryClient();

  return useMutation({
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['documents', orgId] });
      queryClient.invalidateQueries({ queryKey: ['documents', orgId, variables.documentId] });
    },
    mutationFn: async ({ data, documentId }: { documentId: string; data: { content?: string; status?: string } }) => {
      const { data: result } = await clientApi.patch<GeneratedDocumentOut>(
        `${orgUrl(orgId!)}/documents/${documentId}`,
        data,
      );
      return result;
    },
  });
}

function orgUrl(orgId: string) {
  return `/api/v1/organizations/${orgId}`;
}

/**
 * Poll one ActionExecution until it reaches a terminal state, then refresh.
 *
 * Bounded on purpose. A workflow can outlive the page — its own execution
 * timeout is 90 minutes — and a poll without a ceiling would keep a dead tab
 * requesting forever. Giving up quietly is correct: the lists still refresh on
 * window focus, and the execution row shows the real state either way.
 */
async function waitForExecution(orgId: string, executionId: string, onSettled: () => void): Promise<void> {
  const deadline = Date.now() + GENERATION_POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, GENERATION_POLL_INTERVAL_MS));

    try {
      const { data } = await clientApi.get<ActionExecutionOut>(
        `/api/v1/organizations/${orgId}/action-executions/${executionId}`,
      );
      if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled') {
        onSettled();
        return;
      }
    } catch {
      // A transient failure should not abandon the watch; the deadline ends it.
    }
  }
}


export function useWorkflowFailureDetails() {
  const { organization } = useAuth();
  return useMutation({
    mutationFn: async (executionId: string) => {
      if (!organization?.organization_id) throw new Error('No organization selected');
      const { data } = await clientApi.get<{ status: string; message: string; failures: { event_id: number; step: string; error: string }[] }>(
        `${orgUrl(organization.organization_id)}/action-executions/${executionId}/failure-details`,
      );
      return data;
    },
  });
}
